import React, { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getFetch } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { formatShortDateTime, formatNumber, toYmd } from "./faqutil";

/**
 * FAQ 관리 > ③ 통계 (목업 AdminStats) — GET /api/faq/stats?from&to
 *  - 기간 7일/30일/90일 (기본 30일)
 *  - KPI 5: 질문 수 · 답 없음 비율 · 도움됐어요 비율(피드백 남긴 질문 기준) · 캐시 적중률(cache_read>0) · API 비용(USD, 원화 환산 서버 config 환율)
 *  - 일별 질문 수 막대(주말은 연한 색) · 답 없음 질문 TOP(→ 검토 큐) · 많이 매칭된 FAQ TOP · 지점별 · 배포 이력
 */
const FaqStats : React.FC<any> = (props : any) => {

    const [days, setDays] = useState<7 | 30 | 90>(30);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const to = new Date();
            const from = new Date(to.getTime() - (days - 1) * 24 * 3600 * 1000);
            const result : any = await getFetch(`/api/faq/stats?from=${toYmd(from)}&to=${toYmd(to)}`);
            if (result && result.message === "success") {
                setData(result);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        load();
    }, [load, props.refreshKey]);

    const t = data ? data.totals : null;
    const pct = (a : number, b : number) => (b > 0 ? `${Math.round((a / b) * 1000) / 10}%` : "-");

    // 일별: 기간 안의 빈 날짜도 0 으로 채움
    const daily : any[] = [];
    if (data) {
        const map : any = {};
        for (const d of data.daily) map[d.date] = d;
        const start = new Date(data.from + "T00:00:00");
        const end = new Date(data.to + "T00:00:00");
        for (let d = new Date(start); d.getTime() <= end.getTime(); d = new Date(d.getTime() + 24 * 3600 * 1000)) {
            const key = toYmd(d);
            const dow = d.getDay();
            daily.push({ date : key, label : `${d.getMonth() + 1}/${d.getDate()}`, cnt : map[key] ? map[key].cnt : 0, noMatch : map[key] ? map[key].noMatch : 0, weekend : dow === 0 || dow === 6 });
        }
    }

    return (
        <div>
            <div className={styles.section}>
                <div className={styles.toolbar}>
                    {([7, 30, 90] as (7 | 30 | 90)[]).map((d) => (
                        <button key={d} className={`${styles.pill} ${days === d ? styles.pillActive : ""}`} onClick={() => setDays(d)}>{d}일</button>
                    ))}
                    {data ? <span className={styles.caption}>{data.from} ~ {data.to}{loading ? " · 불러오는 중…" : ""} · keep-alive 핑 {formatNumber(data.keepalive.pings)}회(${data.keepalive.costUsd.toFixed(3)})는 질문 수에서 제외</span> : null}
                </div>

                {
                    t ?
                        <div className={styles.kpiGrid}>
                            <div className={styles.kpi}><div className={styles.kpiLabel}>질문 수</div><div className={styles.kpiValue}>{formatNumber(t.questions)}</div><div className={styles.kpiSub}>하루 평균 {formatNumber(Math.round(t.questions / days))}{t.errors > 0 ? ` · 실패 ${t.errors}` : ""}</div></div>
                            <div className={styles.kpi}><div className={styles.kpiLabel}>답 없음 비율</div><div className={styles.kpiValue}>{pct(t.noMatch, t.succeeded)}</div><div className={styles.kpiSub}>{formatNumber(t.noMatch)}건</div></div>
                            <div className={styles.kpi}><div className={styles.kpiLabel}>도움됐어요 비율</div><div className={styles.kpiValue}>{pct(data.feedback.helpful, t.feedbackLogs)}</div><div className={styles.kpiSub}>피드백 남긴 질문 {formatNumber(t.feedbackLogs)}건 기준 · 다른 항목 {data.feedback.wrong_item} · 내용 틀림 {data.feedback.wrong_content} · 답 없음(수동) {data.feedback.no_answer}</div></div>
                            <div className={styles.kpi}><div className={styles.kpiLabel}>캐시 적중률</div><div className={styles.kpiValue}>{pct(t.cacheHits, t.succeeded)}</div><div className={styles.kpiSub}>cache_read &gt; 0 비율{t.avgLatencyMs ? ` · 평균 ${formatNumber(t.avgLatencyMs)}ms` : ""}</div></div>
                            <div className={styles.kpi}><div className={styles.kpiLabel}>API 비용</div><div className={styles.kpiValue}>${(+t.costUsd).toFixed(2)}</div><div className={styles.kpiSub}>약 {formatNumber(Math.round(+t.costUsd * data.krwPerUsd))}원{t.questions > 0 ? ` · 질문당 $${(+t.costUsd / t.questions).toFixed(4)}` : ""}</div></div>
                        </div>
                        : <div className={styles.empty}>{loading ? "불러오는 중…" : "데이터가 없습니다"}</div>
                }
            </div>

            {
                data ?
                    <>
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}><span>일별 질문 수</span><span className={styles.caption}>주말은 연한 색</span></div>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={daily} margin={{ top : 5, right : 10, left : -20, bottom : 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                    <XAxis dataKey="label" tick={{ fontSize : 11, fill : "#6B7280" }} interval={daily.length > 40 ? 6 : daily.length > 14 ? 2 : 0} />
                                    <YAxis allowDecimals={false} tick={{ fontSize : 11, fill : "#6B7280" }} />
                                    <Tooltip formatter={(value : any, name : any) => [`${value}건`, name === "cnt" ? "질문" : "답 없음"]} labelFormatter={(l : any, p : any) => (p && p[0] ? p[0].payload.date : l)} />
                                    <Bar dataKey="cnt" radius={[4, 4, 0, 0]}>
                                        {daily.map((d, i) => <Cell key={i} fill={d.weekend ? "#C7CBE6" : "#3E4DB4"} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={styles.grid2Col}>
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}><span>답 없음 질문 TOP</span><button className={styles.linkBtn} onClick={props.goReview}>검토 큐로</button></div>
                                <table className={styles.table}>
                                    <thead><tr><th>질문 (유사 질문 묶음)</th><th>추천 카테고리</th><th className={styles.num}>횟수</th></tr></thead>
                                    <tbody>
                                        {data.noMatchTop.length === 0 ? <tr><td colSpan={3} className={styles.cellMuted}>없음</td></tr> : data.noMatchTop.map((r : any, i : number) => (
                                            <tr key={i}><td>{r.question}</td><td className={styles.cellSub}>{r.suggestedCategory || "-"}</td><td className={styles.num}><b>{r.cnt}</b></td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}><span>많이 매칭된 FAQ</span></div>
                                <table className={styles.table}>
                                    <thead><tr><th style={{ width : 44 }}>#</th><th>질문</th><th className={styles.num}>매칭</th><th className={styles.num}>👍</th></tr></thead>
                                    <tbody>
                                        {data.topMatched.length === 0 ? <tr><td colSpan={4} className={styles.cellMuted}>없음</td></tr> : data.topMatched.map((r : any) => (
                                            <tr key={r.itemId}><td className={styles.cellMuted}>{r.itemId}</td><td>{r.question}</td><td className={styles.num}>{r.cnt}</td><td className={styles.num}>{r.feedbackTotal > 0 ? pct(r.helpful, r.feedbackTotal) : "-"}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className={styles.grid2Col}>
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}><span>지점별 질문 수</span></div>
                                <table className={styles.table}>
                                    <thead><tr><th>지점</th><th className={styles.num}>질문</th><th className={styles.num}>답 없음</th></tr></thead>
                                    <tbody>
                                        {data.byBranch.length === 0 ? <tr><td colSpan={3} className={styles.cellMuted}>없음</td></tr> : data.byBranch.map((b : any) => (
                                            <tr key={b.branch}><td>{b.branch}</td><td className={styles.num}>{formatNumber(b.cnt)}</td><td className={styles.num}>{formatNumber(b.noMatch)}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}><span>배포 이력</span></div>
                                <table className={styles.table}>
                                    <thead><tr><th>버전</th><th>배포 시각</th><th>배포자</th><th>메모</th><th className={styles.num}>항목</th><th className={styles.num}>토큰</th><th className={styles.num}>단가</th></tr></thead>
                                    <tbody>
                                        {data.releases.length === 0 ? <tr><td colSpan={7} className={styles.cellMuted}>배포 없음</td></tr> : data.releases.map((r : any) => (
                                            <tr key={r.id}><td><b>v{r.version}</b></td><td className={styles.cellMuted}>{formatShortDateTime(r.createdAt)}</td><td>{r.createdBy || "-"}</td><td>{r.memo || "-"}</td><td className={styles.num}>{formatNumber(r.itemCount)}</td><td className={styles.num}>{formatNumber(r.promptTokens)}</td><td className={styles.num}>{r.priceMultiplier > 1 ? `${r.priceMultiplier}배` : "기본"}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                    : null
            }
        </div>
    );
};

export default FaqStats;
